/**
 * UTM Tracking Utility
 * Captures UTM parameters and platform-specific tracking IDs from URL
 * and stores them in sessionStorage to persist across page navigations during the session.
 */

const UTM_STORAGE_KEY = 'horenko_utm_params';

// Standard UTM parameters
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

// Platform-specific tracking IDs
const TRACKING_IDS = [
  'gclid',        // Google Ads Click ID
  'fbclid',       // Facebook Click ID
  'ttclid',       // TikTok Click ID
  'msclkid',      // Microsoft Click ID
  'li_fat_id',    // LinkedIn Click ID
  'twclid',       // Twitter Click ID
  'gbraid',       // Google Ads (Web to App)
  'wbraid',       // Google Ads (App to Web)
];

// All tracking parameters combined
const ALL_TRACKING_PARAMS = [...UTM_PARAMS, ...TRACKING_IDS];

/**
 * Extracts tracking parameters (UTM + platform IDs) from the current URL
 * @returns {Object} Object containing all tracking parameters
 */
export function extractUtmParams() {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const trackingParams = {};

  ALL_TRACKING_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      trackingParams[param] = value;
    }
  });

  return trackingParams;
}

/**
 * Gets stored tracking parameters from sessionStorage
 * @returns {Object} Object containing stored tracking parameters
 */
export function getStoredUtmParams() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to retrieve UTM parameters from sessionStorage:', error);
  }

  return {};
}

/**
 * Stores tracking parameters in sessionStorage
 * Merges new parameters with existing ones (new values take precedence)
 * @param {Object} utmParams - Tracking parameters to store
 */
export function storeUtmParams(utmParams) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const existing = getStoredUtmParams();
    const merged = { ...existing, ...utmParams };

    // Only store if there are actual tracking parameters
    if (Object.keys(merged).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
    }
  } catch (error) {
    console.warn('Failed to store UTM parameters in sessionStorage:', error);
  }
}

/**
 * Captures tracking parameters from URL and stores them
 * Should be called on page load or when URL changes
 */
const SEARCH_ENGINES = ['google', 'bing', 'yahoo', 'duckduckgo', 'yandex', 'ecosia', 'baidu'];

/**
 * Infers source/medium when the landing URL has no utm_source,
 * so organic, referral and direct leads are not left blank.
 * @param {Object} trackingParams - Parameters found in the URL
 * @returns {Object} utm_source / utm_medium to fill in (may be empty)
 */
export function inferSourceParams(trackingParams = {}) {
  if (typeof window === 'undefined' || trackingParams.utm_source) {
    return {};
  }

  if (trackingParams.gclid || trackingParams.gbraid || trackingParams.wbraid) {
    return { utm_source: 'google', utm_medium: 'cpc' };
  }

  let host = '';
  try {
    host = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : '';
  } catch (error) {
    host = '';
  }

  if (!host) {
    return { utm_source: '(direct)', utm_medium: '(none)' };
  }

  // Internal navigation is not a new source
  if (host === window.location.hostname.replace(/^www\./, '')) {
    return {};
  }

  const engine = SEARCH_ENGINES.find((name) => host.split('.').includes(name));
  if (engine) {
    return { utm_source: engine, utm_medium: 'organic' };
  }

  return { utm_source: host, utm_medium: 'referral' };
}

export function captureAndStoreUtmParams() {
  const trackingParams = extractUtmParams();

  // If new tracking parameters are found in URL, store them
  if (Object.keys(trackingParams).length > 0) {
    storeUtmParams({ ...inferSourceParams(trackingParams), ...trackingParams });
    return;
  }

  // First page of the session without tracking params: remember where the visitor came from
  if (Object.keys(getStoredUtmParams()).length === 0) {
    storeUtmParams(inferSourceParams());
  }
}

/**
 * Gets all tracking parameters (from URL or storage)
 * Priority: Current URL > Stored values
 * @returns {Object} Combined tracking parameters (UTM + platform IDs)
 */
export function getAllUtmParams() {
  const urlParams = extractUtmParams();
  const storedParams = getStoredUtmParams();

  // If there are tracking params in URL, use those (and update storage)
  if (Object.keys(urlParams).length > 0) {
    const params = { ...inferSourceParams(urlParams), ...urlParams };
    storeUtmParams(params);
    return params;
  }

  // Otherwise, return stored params
  return storedParams;
}

/**
 * Clears stored tracking parameters from sessionStorage
 */
export function clearUtmParams() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear tracking parameters from sessionStorage:', error);
  }
}
